import type { Track, Profile, PlaybackMode, RatingStatus } from './types.js';
import { getElement } from './dom.js';
import {
  fetchUsers,
  fetchRandomTrack,
  fetchLikedTracks,
  rateTrack as apiRateTrack,
  resetRating as apiResetRating,
  fetchStats,
  fetchRatings,
  searchTracks as apiSearchTracks,
  fetchTrackById,
  addYouTubeLink,
  nukeTrack as apiNukeTrack
} from './api.js';

import {
  isAuthenticated,
  loginWithGoogle,
  logout as authLogout,
  fetchCurrentProfile
} from './auth.js';

import {
  escapeHtml,
  showSeekToast,
  updateStatsUI,
  displayTrack as uiDisplayTrack,
  showFinishedState,
  formatSourceLabel
} from './ui.js';

import { initVisualizer } from './visualizer.js';
import { initWakeLock } from './wakelock.js';

// Application State
let currentTrack: Track | null = null;
let isSubmitting = false;
let playbackMode: PlaybackMode = 'local'; // 'local' | 'all' | 'liked'
let likedQueue: Track[] = [];
let currentQueueIndex = -1;
let activeTab: RatingStatus = 'like';
let currentUser = localStorage.getItem('spootify_user') || null;
let currentAuthProfile: Profile | null = null; // { userId, email, name, avatarUrl }
let allowedUsers: string[] = [];
let searchDebounceTimeout: ReturnType<typeof setTimeout> | undefined;

export function getUserAvatar(username: string | null) {
  const seed = encodeURIComponent((username || 'user').trim().toLowerCase());
  return `/avatars/${seed}.svg`;
}

// DOM Elements
const userModal = getElement('user-modal', HTMLElement);
const headerUserAvatar = getElement('header-user-avatar', HTMLImageElement);
const headerUserName = getElement('header-user-name', HTMLElement);
const btnOpenUserModal = getElement('btn-open-user-modal', HTMLButtonElement);
const btnCloseUserModal = getElement('btn-close-user-modal', HTMLButtonElement);
const btnLogout = getElement('btn-logout', HTMLButtonElement);
const btnGoogleLogin = getElement('btn-google-login', HTMLButtonElement);
const authErrorBanner = getElement('auth-error-banner', HTMLElement);
const playerCard = getElement('player-card', HTMLElement);
const audioPlayer = getElement('audio-player', HTMLAudioElement);
const ytPlayer = getElement('yt-player', HTMLIFrameElement);
const audioContainer = getElement('audio-container', HTMLElement);
const ytContainer = getElement('yt-container', HTMLElement);
const btnNuke = getElement('btn-nuke', HTMLButtonElement);
const nukeShortcutHint = getElement('nuke-shortcut-hint', HTMLElement);

function updateNukeVisibility() {
  const canNuke = currentUser === 'uriel' || currentUser === 'diane';
  const hasLikes = currentTrack && Array.isArray(currentTrack.likedBy) && currentTrack.likedBy.length > 0;

  if (btnNuke) {
    btnNuke.style.display = canNuke ? 'flex' : 'none';
    btnNuke.style.opacity = '';
    btnNuke.disabled = false;
    if (hasLikes && currentTrack) {
      btnNuke.title = `☢️ Attention : déjà liké par ${currentTrack.likedBy.join(', ')} (Cliquer pour confirmer la suppression)`;
      console.log(`[Nuke] Morceau "${currentTrack.title || currentTrack.videoId}" déjà liké par :`, currentTrack.likedBy);
    } else {
      btnNuke.title = "☢️ Completely delete track from database (Press X or 4)";
    }
  }

  if (nukeShortcutHint) {
    nukeShortcutHint.style.display = canNuke ? 'inline' : 'none';
    nukeShortcutHint.style.opacity = '1';
  }
}

function updateHeaderProfile() {
  updateNukeVisibility();
  if (headerUserAvatar) {
    if (currentAuthProfile?.avatarUrl) {
      headerUserAvatar.src = currentAuthProfile.avatarUrl;
      headerUserAvatar.alt = currentUser || 'Avatar';
      headerUserAvatar.style.display = 'block';
    } else if (currentUser) {
      headerUserAvatar.src = getUserAvatar(currentUser);
      headerUserAvatar.alt = currentUser;
      headerUserAvatar.style.display = 'block';
    } else {
      headerUserAvatar.style.display = 'none';
    }
  }
  if (headerUserName) {
    if (currentAuthProfile) {
      const displayName = currentAuthProfile.name || currentUser || 'User';
      headerUserName.textContent = displayName.charAt(0).toUpperCase() + displayName.slice(1);
      headerUserName.title = `${currentAuthProfile.email} (profil: ${currentUser})`;
    } else if (currentUser) {
      headerUserName.textContent = currentUser.charAt(0).toUpperCase() + currentUser.slice(1);
      headerUserName.title = `Profil: ${currentUser}`;
    } else {
      headerUserName.textContent = 'Connexion';
      headerUserName.title = 'Cliquez pour vous connecter';
    }
  }

  if (btnLogout) {
    btnLogout.style.display = isAuthenticated() ? 'inline-flex' : 'none';
  }
}

function showUserModal() {
  if (userModal) userModal.classList.add('active');
}

function hideUserModal() {
  if (userModal) userModal.classList.remove('active');
}

if (btnOpenUserModal) {
  btnOpenUserModal.addEventListener('click', () => {
    showUserModal();
  });
}

if (btnCloseUserModal) {
  btnCloseUserModal.addEventListener('click', () => {
    if (currentUser) hideUserModal();
  });
}

if (headerUserAvatar) {
  headerUserAvatar.addEventListener('click', () => {
    showUserModal();
  });
}

if (btnLogout) {
  btnLogout.addEventListener('click', () => {
    if (confirm('Voulez-vous vous déconnecter de votre compte Spootify ?')) {
      authLogout();
    }
  });
}

if (btnGoogleLogin) {
  btnGoogleLogin.addEventListener('click', async () => {
    try {
      btnGoogleLogin.disabled = true;
      btnGoogleLogin.style.opacity = '0.7';
      if (authErrorBanner) {
        authErrorBanner.style.display = 'none';
        authErrorBanner.textContent = '';
      }

      await loginWithGoogle();

      const profile = await fetchCurrentProfile();
      if (profile?.error) {
        if (authErrorBanner) {
          authErrorBanner.textContent = profile.error;
          authErrorBanner.style.display = 'block';
        }
        authLogout();
        return;
      }

      if (profile?.userId) {
        currentAuthProfile = profile;
        currentUser = profile.userId;
        localStorage.setItem('spootify_user', currentUser);
        updateHeaderProfile();
        hideUserModal();
        await onUserChanged();
      }
    } catch (err) {
      console.error('Erreur lors du login Google SSO:', err);
      if (authErrorBanner) {
        authErrorBanner.textContent = (err instanceof Error ? err.message : '') || 'Erreur lors de la connexion Google SSO';
        authErrorBanner.style.display = 'block';
      }
    } finally {
      btnGoogleLogin.disabled = false;
      btnGoogleLogin.style.opacity = '1';
    }
  });
}

if (userModal) {
  userModal.addEventListener('click', (e) => {
    if (e.target === userModal && currentUser) {
      hideUserModal();
    }
  });
}

const modeLocalBtn = getElement('mode-local', HTMLButtonElement);
const modeAllBtn = getElement('mode-all', HTMLButtonElement);
const modeLikedBtn = getElement('mode-liked', HTMLButtonElement);
const likedPlaylistCard = getElement('liked-playlist-card', HTMLElement);
const likedQueueList = getElement('liked-queue-list', HTMLElement);
const likedQueueSubtitle = getElement('liked-queue-subtitle', HTMLElement);
const btnReshuffleLiked = getElement('btn-reshuffle-liked', HTMLButtonElement);

const btnLike = getElement('btn-like', HTMLButtonElement);
const btnDislike = getElement('btn-dislike', HTMLButtonElement);
const btnSkip = getElement('btn-skip', HTMLButtonElement);

const historyModal = getElement('history-modal', HTMLElement);
const btnOpenHistory = getElement('btn-open-history', HTMLButtonElement);
const btnCloseHistory = getElement('btn-close-history', HTMLButtonElement);
const tabLiked = getElement('tab-liked', HTMLButtonElement);
const tabDisliked = getElement('tab-disliked', HTMLButtonElement);
const historyList = getElement('history-list', HTMLElement);

const searchInput = getElement('search-input', HTMLInputElement);
const btnClearSearch = getElement('btn-clear-search', HTMLButtonElement);
const searchResultsPanel = getElement('search-results-panel', HTMLElement);
const searchResultsList = getElement('search-results-list', HTMLElement);
const searchResultsCount = getElement('search-results-count', HTMLElement);
const btnCloseSearch = getElement('btn-close-search', HTMLButtonElement);

async function onUserChanged() {
  if (!currentUser) {
    showUserModal();
    return;
  }
  hideUserModal();

  const url = new URL(window.location.href);
  url.searchParams.set('user', currentUser);
  if (currentTrack && currentTrack.videoId) {
    url.searchParams.set('v', currentTrack.videoId);
  }
  window.history.replaceState({ videoId: currentTrack ? currentTrack.videoId : null, user: currentUser }, '', url.toString());

  // Refresh user stats
  try {
    const stats = await fetchStats(currentUser);
    updateStatsUI(stats, playbackMode);
  } catch (e) {
    console.error('Failed to fetch stats for new user:', e);
  }

  // If search panel is visible, refresh search for this user
  if (searchInput && searchInput.value && searchResultsPanel && searchResultsPanel.style.display !== 'none') {
    performSearch(searchInput.value);
  }

  // If history modal is open, refresh history for this user
  if (historyModal && historyModal.classList.contains('active')) {
    loadHistory(activeTab);
  }

  // If no track is loaded yet, fetch next track
  if (!currentTrack) {
    if (playbackMode === 'liked') {
      await initLikedPlaylistQueue();
    } else {
      await fetchNextTrack();
    }
  } else if (playbackMode === 'liked') {
    // Refresh liked queue in background for new user without interrupting current playback
    try {
      const likedTracks = await fetchLikedTracks(currentUser);
      likedQueue = likedTracks.sort(() => Math.random() - 0.5);
      renderLikedQueue();
    } catch (e) {
      console.error('Failed to update liked queue for user:', e);
    }
  }
}

// Mode Toggle Listeners
if (modeLocalBtn) {
  modeLocalBtn.addEventListener('click', () => {
    playbackMode = 'local';
    if (likedPlaylistCard) likedPlaylistCard.style.display = 'none';
    modeLocalBtn.classList.add('active');
    if (modeAllBtn) modeAllBtn.classList.remove('active');
    if (modeLikedBtn) modeLikedBtn.classList.remove('active');
    fetchNextTrack();
  });
}

if (modeAllBtn) {
  modeAllBtn.addEventListener('click', () => {
    playbackMode = 'all';
    if (likedPlaylistCard) likedPlaylistCard.style.display = 'none';
    modeAllBtn.classList.add('active');
    if (modeLocalBtn) modeLocalBtn.classList.remove('active');
    if (modeLikedBtn) modeLikedBtn.classList.remove('active');
    fetchNextTrack();
  });
}

if (modeLikedBtn) {
  modeLikedBtn.addEventListener('click', () => {
    playbackMode = 'liked';
    modeLikedBtn.classList.add('active');
    if (modeLocalBtn) modeLocalBtn.classList.remove('active');
    if (modeAllBtn) modeAllBtn.classList.remove('active');
    if (likedPlaylistCard) likedPlaylistCard.style.display = 'flex';
    initLikedPlaylistQueue();
  });
}

if (btnReshuffleLiked) {
  btnReshuffleLiked.addEventListener('click', () => {
    initLikedPlaylistQueue();
  });
}

// Auto-advance when audio track ends
if (audioPlayer) {
  audioPlayer.onended = () => {
    fetchNextTrack();
  };
}

// Fetch next track from server
async function fetchNextTrack() {
  try {
    if (playerCard) playerCard.style.opacity = '0.5';

    if (playbackMode === 'liked') {
      if (likedQueue.length > 0) {
        currentQueueIndex++;
        if (currentQueueIndex >= likedQueue.length) {
          currentQueueIndex = 0;
        }
        currentTrack = likedQueue[currentQueueIndex] ?? null;
        uiDisplayTrack(currentTrack, currentUser, playbackMode);
        renderLikedQueue();
        return;
      } else {
        await initLikedPlaylistQueue();
        return;
      }
    }

    const localOnly = playbackMode === 'local';
    const data = await fetchRandomTrack(currentUser, localOnly);

    updateStatsUI(data.stats, playbackMode);

    if (!data.track) {
      showFinishedState(currentUser, playbackMode);
      return;
    }

    currentTrack = data.track;
    uiDisplayTrack(currentTrack, currentUser, playbackMode);
  } catch (err) {
    console.error('Failed to load track:', err);
    const trackTitle = getElement('track-title', HTMLElement);
    const trackArtist = getElement('track-artist', HTMLElement);
    if (trackTitle) trackTitle.textContent = 'Error loading track';
    if (trackArtist) trackArtist.textContent = 'Check server connection';
  } finally {
    if (playerCard) playerCard.style.opacity = '1';
  }
}

// Liked Playlist Queue Management
async function initLikedPlaylistQueue() {
  likedQueueList.innerHTML = `<div style="text-align: center; color: var(--text-muted); padding: 20px;">Loading randomized queue for ${escapeHtml(currentUser)}...</div>`;
  try {
    likedQueue = await fetchLikedTracks(currentUser);
    likedQueueSubtitle.textContent = `${likedQueue.length} tracks in randomized playback queue (${currentUser})`;

    if (likedQueue.length === 0) {
      likedQueueList.innerHTML = `<div style="text-align: center; color: var(--text-muted); padding: 20px;">No liked tracks found for ${escapeHtml(currentUser)} yet.</div>`;
      return;
    }

    currentQueueIndex = 0;
    currentTrack = likedQueue[0] ?? null;
    uiDisplayTrack(currentTrack, currentUser, playbackMode);
    renderLikedQueue();
  } catch (err) {
    likedQueueList.innerHTML = '<div style="text-align: center; color: #ef4444; padding: 20px;">Failed to load queue</div>';
  }
}

function renderLikedQueue() {
  if (!likedQueue || likedQueue.length === 0) return;

  likedQueueList.innerHTML = likedQueue.map((item, index) => {
    const isCurrent = currentTrack && currentTrack.videoId === item.videoId;
    return `
      <div class="liked-queue-item ${isCurrent ? 'playing' : ''}" id="queue-item-${index}">
        <div class="queue-num">${isCurrent ? '🎵' : `#${index + 1}`}</div>
        <div class="queue-info">
          <div class="queue-title">${escapeHtml(item.title)}</div>
          <div class="queue-artist">
            ${item.isLocal ? '💾 Local' : '🌐 YouTube'} • ${escapeHtml(item.artist || 'Unknown Artist')} • <span style="opacity: 0.75;">${escapeHtml(formatSourceLabel(item.source, item.isLocal))}</span>
          </div>
        </div>
        <div class="queue-actions">
          <button class="btn-search-action btn-search-play" data-action="play-queue" data-index="${index}">▶ Play</button>
          <button class="btn-search-action btn-search-dislike" data-action="unlike-queue" data-video-id="${escapeHtml(item.videoId)}" data-index="${index}">❌ Unlike</button>
        </div>
      </div>
    `;
  }).join('');

  likedQueueList.querySelectorAll('[data-action="play-queue"]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const idx = Number(btn.getAttribute('data-index'));
      playQueueTrack(idx);
    });
  });

  likedQueueList.querySelectorAll('[data-action="unlike-queue"]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const videoId = (btn.getAttribute('data-video-id') || '');
      const idx = Number(btn.getAttribute('data-index'));
      unlikeQueueTrack(videoId, idx);
    });
  });

  if (currentQueueIndex >= 0) {
    const activeElem = document.getElementById(`queue-item-${currentQueueIndex}`);
    if (activeElem) {
      activeElem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
}

function playQueueTrack(index: number) {
  if (index < 0 || index >= likedQueue.length) return;
  currentQueueIndex = index;
  currentTrack = likedQueue[index] ?? null;
  uiDisplayTrack(currentTrack, currentUser, playbackMode);
  renderLikedQueue();
}

async function unlikeQueueTrack(videoId: string, index: number) {
  try {
    const res = await apiResetRating(currentUser, videoId);
    if (res.success) {
      likedQueue.splice(index, 1);
      likedQueueSubtitle.textContent = `${likedQueue.length} tracks in randomized playback queue (${currentUser})`;
      const stats = await fetchStats(currentUser);
      updateStatsUI(stats, playbackMode);

      if (currentTrack && currentTrack.videoId === videoId) {
        fetchNextTrack();
      } else {
        renderLikedQueue();
      }
    }
  } catch (err) {
    console.error('Failed to unlike track:', err);
  }
}

// Search Logic
searchInput.addEventListener('input', (e) => {
  const q = searchInput.value;
  if (q.length > 0) {
    btnClearSearch.style.display = 'block';
  } else {
    btnClearSearch.style.display = 'none';
    searchResultsPanel.style.display = 'none';
    return;
  }

  clearTimeout(searchDebounceTimeout);
  searchDebounceTimeout = setTimeout(() => performSearch(q), 250);
});

btnClearSearch.addEventListener('click', () => {
  searchInput.value = '';
  btnClearSearch.style.display = 'none';
  searchResultsPanel.style.display = 'none';
});

btnCloseSearch.addEventListener('click', () => {
  searchResultsPanel.style.display = 'none';
});

async function performSearch(query: string) {
  if (!query.trim()) return;

  searchResultsList.innerHTML = '<div style="text-align: center; color: var(--text-muted); padding: 20px;">Searching metadata...</div>';
  searchResultsPanel.style.display = 'flex';

  try {
    const results = await apiSearchTracks(currentUser, query);

    searchResultsCount.textContent = `${results.length} tracks found for ${currentUser}`;

    if (results.length === 0) {
      searchResultsList.innerHTML = '<div style="text-align: center; color: var(--text-muted); padding: 20px;">No tracks found matching your query.</div>';
      return;
    }

    searchResultsList.innerHTML = results.map(item => `
      <div class="search-result-item">
        <div class="search-result-info">
          <div class="search-result-title">${escapeHtml(item.title)}</div>
          <div class="search-result-sub">
            ${item.isLocal ? '💾 Local' : '🌐 YouTube'} • ${escapeHtml(item.artist || 'Unknown Artist')} ${item.uploader ? `(${escapeHtml(item.uploader)})` : ''} • <span style="opacity: 0.75;">${escapeHtml(formatSourceLabel(item.source, item.isLocal))}</span>
          </div>
        </div>
        <div class="search-result-actions">
          <button class="btn-search-action btn-search-play" data-action="play-search" data-video-id="${escapeHtml(item.videoId)}">▶ Play</button>
          <button class="btn-search-action btn-search-like ${item.likeStatus === 'like' ? 'active' : ''}" data-action="like-search" data-video-id="${escapeHtml(item.videoId)}">👍 Like</button>
          <button class="btn-search-action btn-search-dislike ${item.likeStatus === 'dislike' ? 'active' : ''}" data-action="dislike-search" data-video-id="${escapeHtml(item.videoId)}">👎 Dislike</button>
        </div>
      </div>
    `).join('');

    searchResultsList.querySelectorAll('[data-action="play-search"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        playSearchResult((btn.getAttribute('data-video-id') || ''));
      });
    });

    searchResultsList.querySelectorAll('[data-action="like-search"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        rateSearchResult((btn.getAttribute('data-video-id') || ''), 'like');
      });
    });

    searchResultsList.querySelectorAll('[data-action="dislike-search"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        rateSearchResult((btn.getAttribute('data-video-id') || ''), 'dislike');
      });
    });

  } catch (err) {
    searchResultsList.innerHTML = '<div style="text-align: center; color: #ef4444; padding: 20px;">Search failed</div>';
  }
}

async function playSearchResult(videoId: string) {
  searchResultsPanel.style.display = 'none';
  try {
    const data = await fetchTrackById(currentUser, videoId);
    if (data.track) {
      currentTrack = data.track;
      uiDisplayTrack(currentTrack, currentUser, playbackMode);
      updateStatsUI(data.stats, playbackMode);
    }
  } catch (err) {
    console.error('Failed to play searched track:', err);
  }
}

async function rateSearchResult(videoId: string, status: RatingStatus) {
  try {
    const res = await apiRateTrack(currentUser, videoId, status);
    if (res.success) {
      const stats = await fetchStats(currentUser);
      updateStatsUI(stats, playbackMode);
      performSearch(searchInput.value);
    }
  } catch (err) {
    console.error('Rating search result failed:', err);
  }
}

// Submitting Ratings
async function submitRating(status: RatingStatus) {
  if (!currentTrack || isSubmitting) return;
  isSubmitting = true;

  try {
    const res = await apiRateTrack(currentUser, currentTrack.videoId, status, currentTrack.title, currentTrack.artist);
    if (res.success) {
      await fetchNextTrack();
    }
  } catch (err) {
    console.error('Rating submission failed:', err);
  } finally {
    isSubmitting = false;
  }
}

function skipTrack() {
  if (isSubmitting) return;
  fetchNextTrack();
}

// Nuke Confirmation Modal Elements
const nukeConfirmModal = getElement('nuke-confirm-modal', HTMLElement);
const nukeConfirmTrackName = getElement('nuke-confirm-track-name', HTMLElement);
const nukeUsersList = getElement('nuke-users-list', HTMLElement);
const btnCancelNuke = getElement('btn-cancel-nuke', HTMLButtonElement);
const btnConfirmNuke = getElement('btn-confirm-nuke', HTMLButtonElement);
const btnCloseNukeModal = getElement('btn-close-nuke-modal', HTMLButtonElement);

function showNukeConfirmModal(track: Track) {
  if (!nukeConfirmModal) return;
  if (nukeConfirmTrackName) {
    nukeConfirmTrackName.textContent = `${track.title || 'Track'} ${track.artist ? `— ${track.artist}` : ''}`;
  }

  if (nukeUsersList && Array.isArray(track.likedBy)) {
    nukeUsersList.innerHTML = track.likedBy.map(u => `
      <div class="nuke-user-chip">
        <img class="nuke-user-avatar" src="${getUserAvatar(u)}" alt="${escapeHtml(u)}" />
        <span class="nuke-user-name">${escapeHtml(u.charAt(0).toUpperCase() + u.slice(1))}</span>
        <span class="nuke-heart-badge">❤️ Like</span>
      </div>
    `).join('');
  }

  nukeConfirmModal.classList.add('active');
}

function hideNukeConfirmModal() {
  if (nukeConfirmModal) nukeConfirmModal.classList.remove('active');
}

async function handleNuke() {
  const canNuke = currentUser === 'uriel' || currentUser === 'diane';
  if (!currentTrack || isSubmitting || !canNuke) return;

  const hasLikes = currentTrack && Array.isArray(currentTrack.likedBy) && currentTrack.likedBy.length > 0;
  if (hasLikes) {
    console.log(`[Nuke confirmation requise] Musique "${currentTrack.title || currentTrack.videoId}" déjà likée par :`, currentTrack.likedBy);
    showNukeConfirmModal(currentTrack);
    return;
  }

  await executeNuke(false);
}

async function executeNuke(force = false) {
  if (!currentTrack || isSubmitting) return;
  hideNukeConfirmModal();
  isSubmitting = true;

  if (btnNuke) {
    btnNuke.disabled = true;
    btnNuke.style.opacity = '0.5';
  }

  const nukedTitle = currentTrack.title || 'Track';
  const nukedVideoId = currentTrack.videoId;

  try {
    showSeekToast(`☢️ Suppression définitive en cours...`);
    const res = await apiNukeTrack(currentUser, nukedVideoId, force);
    if (res.success) {
      showSeekToast(`☢️ Morceau supprimé définitivement : "${nukedTitle}"`);
      if (res.stats) {
        updateStatsUI(res.stats, playbackMode);
      }
      await fetchNextTrack();
    } else {
      showSeekToast(`⚠️ ${res.error || 'Nuke failed'}`);
      console.error('Nuke failed:', res.error);
    }
  } catch (err) {
    console.error('Nuke failed:', err);
  } finally {
    isSubmitting = false;
    if (btnNuke) {
      btnNuke.style.opacity = '';
    }
    updateNukeVisibility();
  }
}

if (btnCancelNuke) {
  btnCancelNuke.addEventListener('click', hideNukeConfirmModal);
}
if (btnCloseNukeModal) {
  btnCloseNukeModal.addEventListener('click', hideNukeConfirmModal);
}
if (btnConfirmNuke) {
  btnConfirmNuke.addEventListener('click', () => {
    executeNuke(true);
  });
}
if (nukeConfirmModal) {
  nukeConfirmModal.addEventListener('click', (e) => {
    if (e.target === nukeConfirmModal) hideNukeConfirmModal();
  });
}

btnLike.addEventListener('click', () => submitRating('like'));
btnDislike.addEventListener('click', () => submitRating('dislike'));
btnSkip.addEventListener('click', () => skipTrack());
if (btnNuke) {
  btnNuke.addEventListener('click', () => handleNuke());
}

// Keyboard Shortcuts & Seek Controls
window.addEventListener('keydown', (e) => {
  if (e.target instanceof HTMLInputElement || historyModal.classList.contains('active')) return;

  const key = e.key;
  const keyLower = key.toLowerCase();

  if (key === 'ArrowLeft' || keyLower === 'arrowleft') {
    e.preventDefault();
    seekPlayback(-5);
  } else if (key === 'ArrowRight' || keyLower === 'arrowright') {
    e.preventDefault();
    seekPlayback(5);
  } else if (keyLower === 'l' || keyLower === '1') {
    e.preventDefault();
    submitRating('like');
  } else if (keyLower === 'd' || keyLower === '2') {
    e.preventDefault();
    submitRating('dislike');
  } else if (keyLower === 's' || keyLower === ' ' || keyLower === '3') {
    e.preventDefault();
    skipTrack();
  } else if ((keyLower === 'x' || keyLower === '4') && (currentUser === 'uriel' || currentUser === 'diane')) {
    e.preventDefault();
    handleNuke();
  }
});

function seekPlayback(seconds: number) {
  if (audioPlayer && audioContainer.style.display !== 'none') {
    const newTime = Math.max(0, Math.min(audioPlayer.duration || Infinity, audioPlayer.currentTime + seconds));
    audioPlayer.currentTime = newTime;
    showSeekToast(seconds);
  } else if (ytPlayer && ytContainer.style.display !== 'none') {
    try {
      ytPlayer.contentWindow?.postMessage(JSON.stringify({
        event: 'command',
        func: 'seekBy',
        args: [seconds]
      }), '*');
    } catch {}
    showSeekToast(seconds);
  }
}

// History Modal Logic
btnOpenHistory.addEventListener('click', () => {
  historyModal.classList.add('active');
  loadHistory(activeTab);
});

btnCloseHistory.addEventListener('click', () => {
  historyModal.classList.remove('active');
});

historyModal.addEventListener('click', (e) => {
  if (e.target === historyModal) historyModal.classList.remove('active');
});

tabLiked.addEventListener('click', () => {
  activeTab = 'like';
  tabLiked.classList.add('active');
  tabDisliked.classList.remove('active');
  loadHistory('like');
});

tabDisliked.addEventListener('click', () => {
  activeTab = 'dislike';
  tabDisliked.classList.add('active');
  tabLiked.classList.remove('active');
  loadHistory('dislike');
});

async function loadHistory(status: RatingStatus) {
  historyList.innerHTML = `<div style="text-align: center; color: var(--text-muted); padding: 20px;">Loading history for ${escapeHtml(currentUser)}...</div>`;

  try {
    const items = await fetchRatings(currentUser, status);

    if (items.length === 0) {
      historyList.innerHTML = `<div style="text-align: center; color: var(--text-muted); padding: 20px;">No ${escapeHtml(status)}d tracks for ${escapeHtml(currentUser)} yet.</div>`;
      return;
    }

    historyList.innerHTML = items.map(item => `
      <div class="history-item">
        <div class="history-info">
          <div class="history-title">${escapeHtml(item.title || 'Untitled Track')}</div>
          <div class="history-artist">${escapeHtml(item.artist || 'Unknown Artist')} (ID: ${escapeHtml(item.video_id)})</div>
        </div>
        <button class="btn-reset" data-action="reset-rating" data-video-id="${escapeHtml(item.video_id)}">Reset</button>
      </div>
    `).join('');

    historyList.querySelectorAll('[data-action="reset-rating"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        handleResetRating((btn.getAttribute('data-video-id') || ''));
      });
    });
  } catch (err) {
    historyList.innerHTML = '<div style="text-align: center; color: #ef4444; padding: 20px;">Failed to load history</div>';
  }
}

async function handleResetRating(videoId: string) {
  try {
    const res = await apiResetRating(currentUser, videoId);
    if (res.success) {
      loadHistory(activeTab);
      const stats = await fetchStats(currentUser);
      updateStatsUI(stats, playbackMode);
      if (!currentTrack) {
        fetchNextTrack();
      }
    }
  } catch (err) {
    console.error('Reset failed:', err);
  }
}

async function loadTrackByIdHandler(videoId: string) {
  try {
    if (playerCard) playerCard.style.opacity = '0.5';
    const data = await fetchTrackById(currentUser, videoId);
    if (data.track) {
      currentTrack = data.track;
      uiDisplayTrack(currentTrack, currentUser, playbackMode, true);
      updateStatsUI(data.stats, playbackMode);
    } else {
      fetchNextTrack();
    }
  } catch (err) {
    console.error('Failed to load track by ID:', err);
    fetchNextTrack();
  } finally {
    if (playerCard) playerCard.style.opacity = '1';
  }
}

// Add YouTube Link Form Elements & Handler
const addLinkInput = getElement('add-link-input', HTMLInputElement);
const btnAddLink = getElement('btn-add-link', HTMLButtonElement);
const addLinkStatus = getElement('add-link-status', HTMLElement);

async function handleAddLink() {
  if (!addLinkInput || !btnAddLink) return;
  const url = addLinkInput.value.trim();
  if (!url) {
    showAddLinkStatus('Please paste a valid YouTube or YouTube Music URL.', 'error');
    return;
  }

  const targetRadio = document.querySelector<HTMLInputElement>('input[name="link-target"]:checked');
  const target = targetRadio?.value === 'playlists' ? 'playlists' : 'tracks';

  btnAddLink.disabled = true;
  btnAddLink.style.opacity = '0.6';

  try {
    const defaultUser = (allowedUsers && allowedUsers.length > 0) ? (allowedUsers[0] ?? 'diane') : 'diane';
    const res = await addYouTubeLink(currentUser || defaultUser, url, target);
    if (res.success) {
      if (res.duplicate) {
        showAddLinkStatus(`⚠️ Link is already present in ${res.filename}.`, 'duplicate');
      } else {
        showAddLinkStatus(`✅ ${res.message || `Successfully added to ${res.filename}!`}`, 'success');
        addLinkInput.value = '';
      }
    } else {
      showAddLinkStatus(`❌ ${res.error || 'Failed to add link.'}`, 'error');
    }
  } catch (err) {
    showAddLinkStatus('❌ Failed to add link. Please check network connection.', 'error');
  } finally {
    btnAddLink.disabled = false;
    btnAddLink.style.opacity = '1';
  }
}

function showAddLinkStatus(msg: string, type: 'error' | 'duplicate' | 'success') {
  if (!addLinkStatus) return;
  addLinkStatus.textContent = msg;
  addLinkStatus.className = `add-link-status ${type}`;
  addLinkStatus.style.display = 'block';
  setTimeout(() => {
    addLinkStatus.style.display = 'none';
  }, 5000);
}

if (btnAddLink) {
  btnAddLink.addEventListener('click', handleAddLink);
}

if (addLinkInput) {
  addLinkInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddLink();
    }
  });
}

// Initial player initialization
async function initPlayer() {
  initVisualizer();
  initWakeLock(audioPlayer, ytPlayer);

  // If authenticated via PocketBase SSO, fetch user profile and mapped userId
  if (isAuthenticated()) {
    try {
      const profile = await fetchCurrentProfile();
      if (profile?.error) {
        if (authErrorBanner) {
          authErrorBanner.textContent = profile.error;
          authErrorBanner.style.display = 'block';
        }
        authLogout();
      } else if (profile?.userId) {
        currentAuthProfile = profile;
        currentUser = profile.userId;
        localStorage.setItem('spootify_user', currentUser);
      }
    } catch (err) {
      console.warn('Failed to verify PocketBase session:', err);
    }
  }

  try {
    const usersData = await fetchUsers();
    if (usersData && Array.isArray(usersData.users) && usersData.users.length > 0) {
      allowedUsers = usersData.users;
    }
  } catch (err) {
    console.error('Failed to fetch allowed users:', err);
  }

  updateHeaderProfile();

  if (!isAuthenticated() || !currentUser) {
    showUserModal();
    return;
  }

  hideUserModal();

  const urlParams = new URLSearchParams(window.location.search);
  const initialVideoId = urlParams.get('v') || urlParams.get('id');
  if (initialVideoId) {
    await loadTrackByIdHandler(initialVideoId);
  } else {
    await fetchNextTrack();
  }
}

window.addEventListener('popstate', async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const videoId = urlParams.get('v') || urlParams.get('id');
  if (videoId && (!currentTrack || currentTrack.videoId !== videoId)) {
    loadTrackByIdHandler(videoId);
  }
});

// Run initialization
initPlayer();
